import React, {CSSProperties, useCallback, useEffect, useState} from "react";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {UseMainLayout} from "../../../context/LayoutContext";
import QModal from "../../../common/components/QModal";
import BodyModalIntegrationComponent from "./components/BodyModalIntegrationComponent";
import {PaymentDetails, PaymentInterface, Variables} from "../../../interfaces/private/PaymentInterface";
import {PaginationInterface, structPagination} from "../../../interfaces/PaginationInterface";
import {ResponseInterface} from "../../../interfaces/ResponseInterface";
import PaymentApi from "../../../common/api/functions/Payment";
import {config} from "../../../config";
import QSelectPerPage from "../../../common/components/Genneral/QSelectPerPage";
import QLoading from "../../../common/components/QLoading";
import QHeaderTable from "../../../common/components/Genneral/QHeaderTable";
import {PaymentConfig} from "./config";
import QPagination from "../../../common/components/QPaging";
import QSearch from "../../../common/components/Genneral/QSearch";
import BodyModalSettingComponent from "./components/BodyModalSettingComponent";
import SetupApi from "../../../common/api/functions/Setup";
import {PaymentSetupInterface} from "../../../interfaces/private/SetupInterface";
import payment from "../../../common/api/functions/Payment";
import BodyModalPaymentDetailComponent from "./components/BodyModalPaymentDetailComponent";
import BodyModalAssignLedgerComponent from "./components/BodyModalAssignLedgerComponent";
import ContentOverlayComponent from "./components/ContentOverlayComponent";
import {useDispatch} from "react-redux";
import {setToast} from "../../../app/rootReducer";

const Payment = () => {
    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();

    const [openInitSetup, setOpenInitSetup] = useState(false);
    const [openModalAssignLedger, setOpenModalAssignLedger] = useState(false);
    const [openModalPaymentSetup, setOpenModalPaymentSetup] = useState(false);
    const [openModalIntegration, setOpenModalIntegration] = useState(false);
    const [openModalLogDetail, setOpenModalLogDetail] = useState(false);
    const [progress, setProgress] = useState(0);

    // Mask API key showing only last 3 characters
    const maskApiKey = (apiKey: string): string => {
        if (!apiKey || apiKey.length <= 3) return apiKey;
        const lastThree = apiKey.slice(-3);
        const maskedPart = '*'.repeat(Math.min(apiKey.length - 3, 20)); // Limit masked part to 20 chars for display
        return maskedPart + lastThree;
    };
    const [payments, setPayments] = useState<Array<PaymentInterface>>([]);
    const [paymentSetup, setPaymentSetup] = useState<PaymentSetupInterface>({
        eco_cashbook_name: "",
        eco_cashbook_number: "",
        eco_credit_account: "",
        eco_debit_account: "",
        eco_diff_currency_account: "",
        eco_fee_account: "",
        last_run: "",
        matching_invoice: false,
        pensopay_api_key: ""
    });
    const [pagination, setPagination] = useState<PaginationInterface | any>({
        ...structPagination, type: 'payment'
    })
    const [loading, setLoading] = useState(true);
    const [loadingLogDetail, setLoadingLogDetail] = useState(false);
    const [loadingSetting, setLoadingSetting] = useState(false);
    const [isSettingLoaded, setIsSettingLoaded] = useState(false);
    const [paymentDetail, setPaymentDetail] = useState<PaymentInterface>({
        _id: 0,
        pensopay_payment_id: 0,
        updated_at: "",
        created_at: "",
        time_ago: "",
        df_status: "",
        eco_matching_invoice: "",
        pensopay_payment_data: {
            account_id: 0,
            acquirer: "",
            amount: 0,
            autocapture: false,
            callback_url: "",
            cancel_url: "",
            captured: 0,
            created_at: "",
            currency: "",
            facilitator: "",
            id: 0,
            link: "",
            link_uuid: "",
            locale: "",
            methods: [],
            order: {
                reference: "",
                payment_details: {} as PaymentDetails,
                basket: [],
                order_id: "",
                refunded: 0,
                remote_ip: "",
                state: "",
                success_url: "",
                testmode: false,
                text_on_statement: "",
                type: "",
                updated_at: "",
                version: 0,
                variables: {} as Variables,
            }

        }
    });
    UseMainLayout();

    useEffect(() => {
        if (loading) {
            loadLog().then(async () => {
                await setLoading(false);
            })
        }
    }, [loading]);

    useEffect(() => {
        loadSetting();
    }, [])

    useEffect(() => {
        // Only check after settings are loaded from API
        if (isSettingLoaded) {
            if(paymentSetup.eco_cashbook_number == "-1" || !paymentSetup.pensopay_api_key){
                setOpenInitSetup(true);
            }else{
                setOpenInitSetup(false);
            }
        }
    }, [paymentSetup.eco_cashbook_number, paymentSetup.pensopay_api_key, isSettingLoaded])



    const loadSetting = async () => {
        setLoadingSetting(true);
        try {
            await SetupApi.detail().then(async (response: ResponseInterface | any) => {
                if (response.status === config.STATUS_CODE.OK) {
                    const data = response?.data.data || []
                    setPaymentSetup({
                            eco_cashbook_name: data?.eco_cashbook_name,
                            eco_cashbook_number: data?.eco_cashbook_number,
                            eco_credit_account: data?.eco_credit_account,
                            eco_debit_account: data?.eco_debit_account,
                            eco_diff_currency_account: data?.eco_diff_currency_account,
                            eco_fee_account: data?.eco_fee_account,
                            matching_invoice: data?.matching_invoice,
                            last_run: data?.last_run,
                            pensopay_api_key: data?.pensopay_api_key
                        }
                    )
                    // Mark that settings are loaded
                    setIsSettingLoaded(true);
                }
            })

        } catch (err) {
        } finally {
            await setLoadingSetting(false);
        }
    };

    const loadLog = async () => {
        try {
            await PaymentApi.getAll(pagination).then(async (response: ResponseInterface | any) => {
                if (response.status === config.STATUS_CODE.OK) {
                    response = response.data.data;
                    setPayments(
                        response?.data || []
                    )
                    setPagination({
                        ...pagination,
                        page: response.current_page,
                        total: response.total,
                        per_page: response.per_page
                    })
                }
            })

        } catch (err) {
        } finally {
            await setLoading(false);
        }
    }

    const callIntegration = async (params?: any) => {
        setProgress(80);
        try {
            const response = await PaymentApi.syncPayment(params);
            setProgress(100);
            await loadLog();
            return response; // Return response with stats to modal
        } catch (error: any) {
            setProgress(0); // Reset progress on error
            console.log('Sync error:', error);
            const errorMessage = error?.data?.message || error?.message || "Sync failed";

            // Show error toast
            dispatch(setToast({
                message: errorMessage,
                type: config.TOAST_TYPE.ERROR
            }));

            // Re-throw to let modal component handle it if needed
            throw error;
        }
    };

    const handleViewLogDetail = (_id: number) => {
        setLoadingLogDetail(true);
        setOpenModalLogDetail(true);
        // LogApi.getDetail(_id).then(async (response: PaymentInterface | any) => {
        //     if (response?.data?.data) {
        //         // setLog(response?.data?.data);
        //         setTimeout(() => {
        //             setLoadingLogDetail(false);
        //         }, 3000);
        //     }
        // })
    };
    // Styles


    return (
        <React.Fragment>
            <ContentOverlayComponent open={openModalAssignLedger} />
            <QModal
                open={openModalIntegration}
                setOpen={setOpenModalIntegration}
                headerContent={'Get payments'}
                bodyContent={(
                    <BodyModalIntegrationComponent
                        showForceImport={true}
                        setOpen={setOpenModalIntegration}
                        open={openModalIntegration}
                        callIntegration={callIntegration}
                        progress={progress}
                    />
                )}
                onClosed={() => setProgress(0)}
                // onOpened={()=>setProgress(0)}

            />
            <QModal
                alwayShow={openInitSetup}
                keyboard={openInitSetup}
                backdrop={openInitSetup?'static':false}
                size={'xl'}
                open={openModalPaymentSetup || openInitSetup}
                setOpen={setOpenModalLogDetail}
                headerContent={'Detail'}
                onClosed={() => {
                }}
                bodyContent={(
                    <BodyModalSettingComponent
                        setOpen={setOpenModalPaymentSetup}
                        // open={openModalIntegration}
                        // setLoading={setLoadingLogDetail}
                        setLoading={setLoadingSetting}
                        // loading={loadingLogDetail}
                        model={paymentSetup}
                        setPaymentSetup={setPaymentSetup}/>
                )}

            />

            <QModal
                size={'xl'}
                open={openModalLogDetail}
                setOpen={setOpenModalLogDetail}
                headerContent={'Detail'}
                // onClosed={()}
                bodyContent={(
                    <BodyModalPaymentDetailComponent
                        setOpen={setOpenModalLogDetail}
                        open={openModalIntegration}
                        setLoading={setLoadingLogDetail}
                        loading={loadingLogDetail}
                        log={paymentDetail}
                    />
                )}

            />

            <div className="col-md-12">

                <div className="row ">
                    <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <div className="page-title-name">Payments</div>
                            <div className="page-title-right">
                                <div className="d-flex">
                                    <a href="#" className="btn btn-sm  bg-orange-zp ml-3"
                                       onClick={() => setOpenModalIntegration(true)}><span><i
                                        className="lab la-telegram mr-1"></i> Manual get payments</span></a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="business-detail bg-orange-bland-zp bdr-20 pd-25">
                            <div className="row">
                                <div className="col-md-8">
                                    <h5 className="mb-2">Setting</h5>

                                </div>
                                <div className="col-md-4 text-end">
                                    <a className={""} href="#" data-bs-toggle="modal"
                                       data-bs-target="#showModalBusinessDetail"><i
                                        className="las la-pen fs-24 cl-br-ams ml-2"
                                        onClick={() => {
                                            setOpenModalPaymentSetup(true);
                                        }}
                                    /></a>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4 mb-2">
                                    <span><strong>Cashbook</strong>:</span> {paymentSetup.eco_cashbook_name ?? 'n/a'}</div>
                                <div className="col-md-4 mb-2">
                                    <span><strong>Debit account</strong>:</span> {paymentSetup.eco_debit_account ?? 'n/a'}</div>
                               <div className="col-md-4 mb-2">
                                    <span><strong>Credit account</strong>:</span> {paymentSetup.eco_credit_account ?? 'n/a'}</div>
                                <div className="col-md-4 mb-2">
                                    <span><strong>Fee account</strong>:</span> {paymentSetup.eco_fee_account ?? 'n/a'}</div>
                                <div className="col-md-4 mb-2">
                                    <span><strong>Difference currency account</strong>:</span> {paymentSetup.eco_diff_currency_account ?? 'n/a'}
                                </div>
                               {/*<div className="col-md-4 mb-2">*/}
                                <div className="col-md-4 mb-2">
                                         <span><strong>Matching invoice</strong>:</span> {paymentSetup.matching_invoice ? 'Yes' : 'No'}
                                </div>

                                <div className="col-md-4 mb-2">
                                    <span><strong>Pensopay API key</strong>:</span> {paymentSetup.pensopay_api_key ? maskApiKey(paymentSetup.pensopay_api_key) : 'n/a'}</div>
                                {/* <div className="col-md-4 mb-2">
                                    <span><strong>Ledgers </strong>:</span> {paymentSetup.ledgers ?? 'n/a'}
                                </div> */}
                                <div className="col-md-4 mb-2">
                                    <span><strong >Last run</strong>: </span>{(moment(paymentSetup.last_run)).isValid() && moment(paymentSetup.last_run).format('DD.MM.YY') || 'n/a'}</div>

                            </div>
                            {/*<div className="row">

                            </div>*/}
                        </div>
                    </div>
                    <div className="col-lg-12">
                        <div className="mb-3">
                            <div className="card-header align-items-center d-flex">
                                <div className="mb-0 flex-grow-1">
                                    <ul className="nav nav-tabs nav-tabs-custom nav-success bd-none" role="tablist">
                                    </ul>
                                </div>
                                <QSearch
                                    onChange={async (value: string) => {
                                        await setPagination({
                                            ...pagination,
                                            search: value,
                                        })
                                        await setLoading(true);
                                    }}
                                />
                                <QSelectPerPage pagination={pagination} setPagination={setPagination}
                                                setLoading={setLoading}/>
                            </div>
                            <QLoading loading={loading || loadingSetting || loadingLogDetail}/>
                            <table className="table align-middle table-nowrap mb-0" id="customerTable">
                                <QHeaderTable
                                    paging={pagination}
                                    setPaging={async (p: any) => {
                                        await setPagination(p);
                                        await setLoading(true);
                                    }}
                                    headers={PaymentConfig.headers(t)}
                                    funcChecked={() => {
                                    }}
                                />
                                <tbody className="list form-check-all">
                                {payments?.map((payment: PaymentInterface, index: number) => {
                                    // let checkedLogIndex: any | LogInterface = logChecked.arrOrder?.findIndex((o: number) => o === log._id);
                                    return (
                                        <tr key={index}>
                                            {/*<th scope="row">*/}
                                            {/*    <div className="form-check">*/}
                                            {/*        <input*/}
                                            {/*            className="form-check-input chk_child"*/}
                                            {/*            type="checkbox"*/}
                                            {/*            name="chk_child"*/}
                                            {/*            // checked={checkedLogIndex > -1}*/}
                                            {/*            defaultValue="option1"*/}
                                            {/*            onClick={(event: any) => {*/}

                                            {/*            }}*/}
                                            {/*        />*/}
                                            {/*    </div>*/}
                                            {/*</th>*/}
                                            <td className={'text-left'}> {payment.pensopay_payment_id}</td>
                                            <td> {payment.pensopay_payment_data?.reference}</td>
                                            <td> {payment.pensopay_payment_data?.acquirer}</td>
                                            <td className={'text-right'}> {payment.pensopay_payment_data?.amount ? payment.pensopay_payment_data.amount / 100 : 'n/a'}</td>
                                            <td className={'text-center'}>{(moment(payment.pensopay_payment_data?.created_at)).isValid() && moment(payment.pensopay_payment_data?.created_at).format('DD.MM.YY')}</td>
                                            {/* <td className={'text-right'}> {payment.eco_matching_invoice}</td> */}
                                            <td>  {payment.df_message?.substring(0, 100)}</td>
                                            <td className={'text-center'}>
                                              <span className={{
                                                  success: "text-success",
                                                  failed: "text-danger",
                                                  notice: "text-warning"
                                              }[payment.df_status] || "text-secondary"}>
                                                {payment.df_status}
                                              </span>
                                            </td>
                                            <td>  {payment.time_ago}</td>
                                            <td className="text-end">
                                                <a href={'#'} onClick={(e) => {
                                                    e.preventDefault();
                                                    // setLoadingLogDetail(true);
                                                    setOpenModalLogDetail(true);
                                                    setPaymentDetail(payment);
                                                    // handleViewLogDetail(payment._id);
                                                }}>
                                                    <i className="las la-chevron-right fs-16 cl-br-ams ml-2"/>
                                                </a>

                                            </td>
                                        </tr>
                                    )
                                })}
                                </tbody>
                            </table>
                        </div>
                        <QPagination
                            pagination={pagination}
                            setPagination={setPagination}
                            setLoading={setLoading}
                        />
                    </div>
                </div>
            </div>

        </React.Fragment>
    )
}
export default React.memo(Payment);
